import { useState, useEffect, useRef, useCallback } from 'react';
import { proposalService } from '../../../services/proposal.service';
import type { HistorySession } from '../../../services/proposal.service';
import { alumniService } from '../../../services/alumni.service';
import posthog from '../../../posthog';

export type MatchStep = 'selector' | 'loading' | 'configure' | 'proposals';

const HELPER_TEXTS = [
  'Analyzing philanthropic interests...',
  'Reviewing project requirements...',
  'Matching impact areas...',
  'Estimating donor-project alignment...',
  'Generating recommendations...',
  'Scoring semantic similarity...',
  'Applying cross-encoder re-ranking...',
];

export function useProposalMatcher() {
  const [step, setStep] = useState<MatchStep>('selector');

  // Step 1: Donor Selection
  const [searchTerm, setSearchTerm] = useState('');
  const [alumniList, setAlumniList] = useState<any[]>([]);
  const [loadingAlumni, setLoadingAlumni] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [selectedAlumni, setSelectedAlumni] = useState<any | null>(null);

  // Step 2: Loading / Research
  const [researchStep, setResearchStep] = useState(0); // 0..3 maps to LOADING_STEPS
  const [researchData, setResearchData] = useState<any | null>(null);
  const [helperTextIdx, setHelperTextIdx] = useState(0);
  const helperInterval = useRef<ReturnType<typeof setInterval> | null>(null);

  // Step 3: Configure / Preferences
  const [domain, setDomain] = useState('');
  const [budgetText, setBudgetText] = useState('');
  const [focus, setFocus] = useState('');
  const [notes, setNotes] = useState('');
  const [count, setCount] = useState(5);
  const [donorInsight, setDonorInsight] = useState('');
  const [isAutofilling, setIsAutofilling] = useState(false);

  // Step 4: Results
  const [proposals, setProposals] = useState<any[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [sessionId, setSessionId] = useState<string | null>(null);

  // Sidebar / History
  const [historySessions, setHistorySessions] = useState<HistorySession[]>([]);
  const [historyOpen, setHistoryOpen] = useState(true);

  const wsRef = useRef<WebSocket | null>(null);

  // Load history list on mount
  const fetchHistory = useCallback(async () => {
    try {
      const data = await proposalService.getHistory();
      if (data.success) {
        setHistorySessions(data.sessions || []);
      }
    } catch {}
  }, []);

  useEffect(() => {
    fetchHistory();
    return () => {
      wsRef.current?.close();
      if (helperInterval.current) clearInterval(helperInterval.current);
    };
  }, [fetchHistory]);

  // Debounced search for alumni
  useEffect(() => {
    if (!searchTerm.trim()) {
      setAlumniList([]);
      return;
    }
    const timer = setTimeout(async () => {
      setLoadingAlumni(true);
      try {
        const data = await alumniService.getAlumniList({ q: searchTerm, limit: 8 });
        setAlumniList(data.alumni || []);
      } catch (err) {
        console.error(err);
      } finally {
        setLoadingAlumni(false);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Rotate helper loading text
  useEffect(() => {
    if (step === 'loading') {
      helperInterval.current = setInterval(() => {
        setHelperTextIdx((i) => (i + 1) % HELPER_TEXTS.length);
      }, 2500);
    } else {
      if (helperInterval.current) {
        clearInterval(helperInterval.current);
      }
    }
    return () => {
      if (helperInterval.current) clearInterval(helperInterval.current);
    };
  }, [step]);

  const resetSession = () => {
    wsRef.current?.close();
    setStep('selector');
    setSelectedAlumni(null);
    setSearchTerm('');
    setResearchData(null);
    setResearchStep(0);
    setDomain('');
    setBudgetText('');
    setFocus('');
    setNotes('');
    setCount(5);
    setDonorInsight('');
    setProposals([]);
    setIsSearching(false);
    setSessionId(null);
  };

  const saveProgress = useCallback(async (currentStepStr: string, updatedResearch?: any) => {
    try {
      const data = await proposalService.saveProgress({
        session_id: sessionId,
        current_step: currentStepStr,
        person_data: selectedAlumni,
        web_research_data: updatedResearch ?? researchData,
        form_data: { domain, budget_text: budgetText, focus, count },
      });
      if (data.success && data.session_id) {
        setSessionId(data.session_id);
        fetchHistory();
      }
    } catch {}
  }, [sessionId, selectedAlumni, researchData, domain, budgetText, focus, count, fetchHistory]);

  const triggerAutofill = useCallback(async (data: any) => {
    setIsAutofilling(true);
    setResearchStep(3);
    try {
      const name = selectedAlumni?.source_info?.current_name || selectedAlumni?.source_info?.csv_name || selectedAlumni?.linkedin_info?.name || selectedAlumni?.linkedin_profile?.name || 'Alumni';
      const result = await proposalService.autofillConfig({
        structured_data: data,
        person: {
          name,
          company: selectedAlumni?.source_info?.company,
          designation: selectedAlumni?.source_info?.designation,
          degree: selectedAlumni?.source_info?.degree,
          department: selectedAlumni?.source_info?.department,
          batch: selectedAlumni?.source_info?.batch,
        },
      });
      if (result.success) {
        setDomain(result.domain || 'Miscellaneous');
        setBudgetText(result.budget || '50 Lakhs');
        setFocus(result.interest_focus || '');
        setDonorInsight(result.donor_insight || '');
      }
    } catch {} finally {
      setIsAutofilling(false);
      setStep('configure');
    }
  }, [selectedAlumni]);

  const startWebResearch = useCallback(() => {
    const alumniId = selectedAlumni?.donor_id || selectedAlumni?.source_info?.alumni_id || selectedAlumni?.source_info?.alumni_csr_id;
    if (!alumniId) {
      alert('Selected alumnus does not have a valid identifier.');
      setStep('selector');
      return;
    }

    setResearchData(null);
    setResearchStep(0);

    wsRef.current?.close();
    const wsProtocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    const wsHost = import.meta.env.DEV ? 'localhost:8000' : window.location.host;
    const basePath = (import.meta.env.VITE_BASE_PATH || '/').replace(/\/$/, '');
    const ws = new WebSocket(`${wsProtocol}//${wsHost}${basePath}/api/deepsearch/ws/full/${alumniId}`);
    
    wsRef.current = ws;

    ws.onmessage = (event) => {
      try {
        const payload = JSON.parse(event.data);
        if (payload.type === 'progress') {
          const mappedStep = Math.min(payload.step, 3);
          setResearchStep(mappedStep);
        } else if (payload.type === 'result') {
          setResearchData(payload.data);
          setResearchStep(3);
          saveProgress('loading', payload.data);
          triggerAutofill(payload.data);
        } else if (payload.type === 'error') {
          alert(`Research failed: ${payload.message}`);
          setStep('selector');
        }
      } catch {}
    };

    ws.onerror = () => {
      alert('Connection to research server lost. Please try again.');
      setStep('selector');
    };
  }, [selectedAlumni, saveProgress, triggerAutofill]);

  const loadHistorySession = async (sessId: string) => {
    try {
      const data = await proposalService.getSessionDetails(sessId);
      if (data.success) {
        setSessionId(sessId);
        setSelectedAlumni(data.person_data);
        setResearchData(data.web_research_data);
        if (data.conversation_state) {
          setDomain(data.conversation_state.domain || '');
          setFocus(data.conversation_state.focus || '');
          setCount(data.conversation_state.count || 5);
        }
        if (data.form_data) {
          setBudgetText(data.form_data.budget_text || '');
        }
        if (data.donor_insight) setDonorInsight(data.donor_insight);
        if (data.proposals && data.proposals.length > 0) {
          setProposals(data.proposals);
          setStep('proposals');
        } else {
          setStep('configure');
        }
      }
    } catch {}
  };

  const deleteHistorySession = async (sessId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    if (!window.confirm('Delete this saved mapping?')) return;
    try {
      const data = await proposalService.deleteSession(sessId);
      if (data.success) {
        if (sessionId === sessId) resetSession();
        fetchHistory();
      }
    } catch {}
  };

  const handleStartLoading = () => {
    if (!selectedAlumni) return;
    setStep('loading');
    setResearchStep(0);
    startWebResearch();
  };

  const handleSelectAlumni = (alumni: any) => {
    setSelectedAlumni(alumni);
    setSearchTerm(alumni.source_info?.current_name || alumni.source_info?.csv_name || alumni.linkedin_info?.name || alumni.linkedin_profile?.name || 'Alumni');
    setDropdownOpen(false);
    setAlumniList([]);
  };

  const runProposalSearch = async () => {
    try {
      const res = await proposalService.searchProposals({
        domain,
        budget_text: budgetText,
        focus: focus + (notes ? `\n\nAdditional context: ${notes}` : ''),
        count,
        session_id: sessionId,
        person_data: selectedAlumni,
        web_research_data: researchData,
        donor_insight: donorInsight,
      });

      if (!res.ok || !res.body) throw new Error('Search failed');

      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      let accumulated = '';
      let recommendationCount = 0;

      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() || '';

        for (const line of lines) {
          if (!line.trim()) continue;
          try {
            const parsed = JSON.parse(line);
            if (parsed.session_id) {
              setSessionId(parsed.session_id);
              fetchHistory();
            } else if (parsed.proposals) {
              recommendationCount = parsed.proposals.length;
              setProposals(parsed.proposals);
            } else if (parsed.chunk) {
              accumulated += parsed.chunk;
            }
          } catch {}
        }
      }

      if (buffer.trim()) {
        try {
          const parsed = JSON.parse(buffer);
          if (parsed.proposals) {
            recommendationCount = parsed.proposals.length;
            setProposals(parsed.proposals);
          }
          if (parsed.session_id) setSessionId(parsed.session_id);
        } catch {}
      }
      posthog.capture('proposal_match_completed', { recommendation_count: recommendationCount });
    } catch (e) {
      console.error(e);
      alert('Proposal search failed. Please try again.');
    } finally {
      setIsSearching(false);
    }
  };

  const handleGenerateMatches = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!domain || !budgetText) return;
    posthog.capture('proposal_match_requested', { domain, recommendation_count: count });
    setStep('proposals');
    setIsSearching(true);
    setProposals([]);
    saveProgress('proposals');
    await runProposalSearch();
  };

  return {
    step,
    setStep,
    searchTerm,
    setSearchTerm,
    alumniList,
    loadingAlumni,
    dropdownOpen,
    setDropdownOpen,
    selectedAlumni,
    handleSelectAlumni,
    handleStartLoading,
    researchStep,
    researchData,
    helperTextIdx,
    helperTexts: HELPER_TEXTS,
    domain,
    setDomain,
    budgetText,
    setBudgetText,
    focus,
    setFocus,
    notes,
    setNotes,
    count,
    setCount,
    donorInsight,
    isAutofilling,
    proposals,
    isSearching,
    sessionId,
    historySessions,
    historyOpen,
    setHistoryOpen,
    resetSession,
    loadHistorySession,
    deleteHistorySession,
    handleGenerateMatches,
  };
}
